import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { CheckCircle2, XCircle, Loader2, Clock, ShieldCheck } from "lucide-react";
import { BrandMark } from "@/lib/branding";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * /estimate-discount-approval/:token — public page opened by Rui Pinto or
 * Daniela Monteiro from the approval email. Any single approval applies
 * the discount to the estimate; rejection cancels the request.
 */
export default function PublicDiscountApproval() {
  const { token } = useParams();
  const [d, setD] = useState(null);
  const [error, setError] = useState(null);
  const [approverEmail, setApproverEmail] = useState("");
  const [reasonText, setReasonText] = useState("");
  const [submitting, setSubmitting] = useState(null); // "approve" | "reject"
  const [decided, setDecided] = useState(null); // {status, request}

  const load = async () => {
    try {
      const r = await axios.get(`${BACKEND_URL}/api/public/estimate-discount-approval/${token}`);
      setD(r.data);
    } catch (e) {
      setError(e.response?.data?.detail || "Pedido não encontrado");
    }
  };
  useEffect(() => { load(); }, [token]);

  const submit = async (decision) => {
    if (!approverEmail) { toast.error("Escolha o seu email para prosseguir."); return; }
    if (decision === "reject" && !reasonText.trim()) {
      toast.error("Motivo obrigatório ao recusar."); return;
    }
    setSubmitting(decision);
    try {
      const r = await axios.post(
        `${BACKEND_URL}/api/public/estimate-discount-approval/${token}/decide`,
        { decision, approver_email: approverEmail, decision_reason: reasonText.trim() || null },
      );
      setDecided({ status: r.data.status, request: r.data.request });
      toast.success(decision === "approve" ? "Aprovado — desconto aplicado." : "Pedido recusado.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao decidir");
    } finally { setSubmitting(null); }
  };

  if (error) return (
    <div className="min-h-screen bg-background flex items-center justify-center text-muted-foreground font-body p-8" data-testid="discount-error">
      {error}
    </div>
  );
  if (!d) return (
    <div className="min-h-screen bg-background flex items-center justify-center text-muted-foreground font-body">
      <Loader2 className="w-4 h-4 animate-spin mr-2" /> A carregar…
    </div>
  );

  const req = decided?.request || d.request;
  const lead = d.lead || {};
  const fmt = (v) => (Number(v) || 0).toLocaleString("pt-PT", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const suffix = req.mode === "pontual" ? " total" : " /mês";
  const pending = req.status === "pending";
  const approved = req.status === "approved";
  const rejected = req.status === "rejected";
  const expired = req.status === "expired";
  const cancelled = req.status === "cancelled";

  return (
    <div className="min-h-screen bg-background text-foreground font-body" data-testid="discount-approval-root">
      <header className="sticky top-0 z-10 bg-card/80 backdrop-blur-xl border-b border-border">
        <div className="max-w-3xl mx-auto px-6 py-5 flex items-center justify-between">
          <BrandMark size={36} />
          <div className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground font-semibold">
            Aprovação · Redução de estimativa
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-14 lg:py-20 space-y-8">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-[hsl(var(--warning))] font-semibold mb-4">
            Caring · Pedido de redução
          </div>
          <h1 className="font-display text-4xl lg:text-5xl leading-tight text-primary tracking-tight">
            {lead.contact_name || "Cliente"}
          </h1>
          {lead.assigned_manager_name && (
            <div className="text-sm text-muted-foreground mt-3">
              Gestor: <b>{lead.assigned_manager_name}</b>
            </div>
          )}
        </div>

        {/* Status banner (if already decided or expired) */}
        {approved && (
          <div className="rounded-2xl border border-[hsl(var(--success))]/40 bg-[hsl(var(--success-bg))] p-6 flex items-start gap-3" data-testid="status-approved">
            <CheckCircle2 className="w-6 h-6 text-[hsl(var(--success))] shrink-0" />
            <div>
              <div className="font-ui text-lg text-[hsl(var(--success))]">Aprovado por {req.decided_by_name}</div>
              <div className="text-sm text-[hsl(var(--success))] mt-1">O desconto foi aplicado automaticamente à estimativa deste lead.</div>
            </div>
          </div>
        )}
        {rejected && (
          <div className="rounded-2xl border border-[hsl(var(--danger))]/40 bg-[hsl(var(--danger-bg))] p-6 flex items-start gap-3" data-testid="status-rejected">
            <XCircle className="w-6 h-6 text-[hsl(var(--danger))] shrink-0" />
            <div>
              <div className="font-ui text-lg text-[hsl(var(--danger))]">Recusado por {req.decided_by_name}</div>
              {req.decision_reason && <div className="text-sm text-[hsl(var(--danger))] mt-1">Motivo: {req.decision_reason}</div>}
            </div>
          </div>
        )}
        {expired && (
          <div className="rounded-2xl border border-[hsl(var(--warning))]/40 bg-[hsl(var(--warning-bg))] p-6 flex items-start gap-3" data-testid="status-expired">
            <Clock className="w-6 h-6 text-[hsl(var(--warning))] shrink-0" />
            <div>
              <div className="font-ui text-lg text-[hsl(var(--warning))]">Pedido caducado</div>
              <div className="text-sm text-[hsl(var(--warning))] mt-1">Passaram mais de 48h — peça ao gestor para submeter novo pedido.</div>
            </div>
          </div>
        )}
        {cancelled && (
          <div className="rounded-2xl border border-border bg-card p-6 flex items-start gap-3" data-testid="status-cancelled">
            <div className="w-6 h-6 text-muted-foreground shrink-0">—</div>
            <div>
              <div className="font-ui text-lg text-foreground">Pedido cancelado</div>
              <div className="text-sm text-muted-foreground mt-1">Substituído por um pedido mais recente.</div>
            </div>
          </div>
        )}

        {/* Value comparison */}
        <div className="bg-card border border-border rounded-2xl p-8" data-testid="value-comparison">
          <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-semibold mb-4">Comparação</div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="rounded-xl bg-background p-5">
              <div className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground font-semibold mb-1">Valor atual</div>
              <div className="font-ui text-2xl text-foreground tabular-nums">{fmt(req.current_value)} €<span className="text-sm text-muted-foreground">{suffix}</span></div>
            </div>
            <div className="rounded-xl bg-[hsl(var(--warning-bg))] p-5 border border-[hsl(var(--warning))]/40">
              <div className="text-[10px] uppercase tracking-[0.15em] text-primary font-semibold mb-1">Valor solicitado</div>
              <div className="font-ui text-2xl text-primary tabular-nums">{fmt(req.requested_value)} €<span className="text-sm text-[hsl(var(--warning))]">{suffix}</span></div>
            </div>
            <div className="rounded-xl bg-[hsl(var(--danger-bg))] p-5 border border-[hsl(var(--danger))]/40">
              <div className="text-[10px] uppercase tracking-[0.15em] text-[hsl(var(--danger))] font-semibold mb-1">Delta</div>
              <div className="font-ui text-2xl text-[hsl(var(--danger))] tabular-nums">
                {(req.delta > 0 ? "+" : "")}{fmt(req.delta)} €
              </div>
              <div className="text-xs text-[hsl(var(--danger))] mt-1">
                {req.current_value ? ((req.delta / req.current_value) * 100).toFixed(1) : "0"}%
              </div>
            </div>
          </div>
        </div>

        {/* Reason */}
        <div className="bg-card border border-border rounded-2xl p-6" data-testid="reason-block">
          <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-semibold mb-2">Motivo indicado pelo gestor</div>
          <div className="text-sm text-foreground leading-relaxed whitespace-pre-wrap">{req.reason || "—"}</div>
          <div className="text-xs text-muted-foreground mt-3">
            Pedido submetido por <b>{req.requested_by_name || "gestor"}</b> em {new Date(req.requested_at).toLocaleString("pt-PT")}.
          </div>
        </div>

        {/* Decision panel (only if pending) */}
        {pending && (
          <div className="bg-card border-2 border-primary rounded-2xl p-6" data-testid="decision-panel">
            <div className="text-[10px] uppercase tracking-[0.2em] text-primary font-semibold mb-4 flex items-center gap-2">
              <ShieldCheck className="w-4 h-4" /> Decida sobre este pedido
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-[10px] uppercase tracking-[0.15em] text-muted-foreground font-semibold mb-1.5">
                  Quem está a decidir?
                </label>
                <div className="flex flex-wrap gap-2">
                  {(d.approver_emails || []).map((em) => (
                    <button
                      key={em}
                      type="button"
                      onClick={() => setApproverEmail(em)}
                      className={`px-4 py-2 rounded-full text-xs border transition-colors ${approverEmail === em
                        ? "bg-primary text-white border-primary"
                        : "bg-card text-foreground border-border hover:bg-background"}`}
                      data-testid={`approver-${em}`}
                    >
                      {em}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="block text-[10px] uppercase tracking-[0.15em] text-muted-foreground font-semibold mb-1.5">
                  Comentário / motivo (obrigatório se recusar)
                </label>
                <textarea
                  value={reasonText}
                  onChange={(e) => setReasonText(e.target.value)}
                  rows={3}
                  className="w-full bg-card border border-border rounded-md px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-[#1E3A8A]/20 resize-y"
                  placeholder="Ex: promoção interna aprovada em reunião do dia X"
                  data-testid="decision-reason"
                />
              </div>
              <div className="flex flex-wrap items-center gap-3 pt-2">
                <button
                  disabled={!approverEmail || submitting !== null}
                  onClick={() => submit("approve")}
                  className="inline-flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold rounded-full px-6 py-3 text-sm disabled:opacity-40 disabled:cursor-not-allowed"
                  data-testid="btn-approve"
                >
                  {submitting === "approve" ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
                  Aprovar redução
                </button>
                <button
                  disabled={!approverEmail || submitting !== null}
                  onClick={() => submit("reject")}
                  className="inline-flex items-center gap-2 bg-rose-600 hover:bg-rose-700 text-white font-semibold rounded-full px-6 py-3 text-sm disabled:opacity-40 disabled:cursor-not-allowed"
                  data-testid="btn-reject"
                >
                  {submitting === "reject" ? <Loader2 className="w-4 h-4 animate-spin" /> : <XCircle className="w-4 h-4" />}
                  Recusar
                </button>
                <div className="text-xs text-muted-foreground">Basta 1 aprovação (Rui ou Daniela).</div>
              </div>
            </div>
          </div>
        )}

        <div className="text-xs text-muted-foreground text-center pt-4">
          Este link caduca em 48h após criação · Caring · Aprovação interna
        </div>
      </main>
    </div>
  );
}
